// features/visitor/visitorLimitsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate } from '../hooks/axiosInstance';

// Get visitor statistics for current user
export const getVisitorStats = createAsyncThunk(
  'visitorLimits/getVisitorStats',
  async (_, thunkAPI) => {
    try {
      const response = await axiosPrivate.get('/visitor-stats/current');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Get visitor configuration (limits from UserMeta)
export const getVisitorConfig = createAsyncThunk(
  'visitorLimits/getVisitorConfig',
  async (_, thunkAPI) => {
    try {
      const response = await axiosPrivate.get('/visitor-config');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Update visitor configuration
export const updateVisitorConfig = createAsyncThunk(
  'visitorLimits/updateVisitorConfig',
  async (configData, thunkAPI) => {
    try {
      const response = await axiosPrivate.put('/visitor-config', configData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Get visitors list
export const getVisitorsList = createAsyncThunk(
  'visitorLimits/getVisitorsList',
  async (_, thunkAPI) => {
    try {
      const response = await axiosPrivate.get('/visitors/list');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Reset visitor limits (admin only)
export const resetVisitorLimits = createAsyncThunk(
  'visitorLimits/resetVisitorLimits',
  async (userId, thunkAPI) => {
    try {
      const response = await axiosPrivate.post(`/visitor/${userId}/reset-limits`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

const visitorLimitsSlice = createSlice({
  name: 'visitorLimits',
  initialState: {
    // Current user's stats - NO hardcoded defaults
    currentStats: {
      requestCount: 0,
      limit: null,
      rateLimit: null,
      lastReset: null,
      nextReset: null,
      recentRequests: []
    },
    // Configuration from UserMeta - NO hardcoded defaults
    config: {
      visitorRequestLimit: null,
      visitorRateLimit: null,
      trackVisitorRequests: null
    },
    // Visitors list
    visitors: [],
    visitorsTotal: 0,
    // UI state
    loading: false,
    configLoading: false,
    visitorsLoading: false,
    error: null,
    success: null,
    // Calculated values - will be calculated from real data
    remaining: 0,
    percentage: 0,
    isNearLimit: false,
    isOverLimit: false
  },
  reducers: {
    // Clear error and success messages
    clearMessages: (state) => {
      state.error = null;
      state.success = null;
    },
    // Update stats locally (when making requests)
    incrementLocalCount: (state) => {
      const limit = state.currentStats.limit || state.config.visitorRequestLimit;
      if (limit && state.currentStats.requestCount < limit) {
        state.currentStats.requestCount += 1;
        state.remaining = limit - state.currentStats.requestCount;
        state.percentage = (state.currentStats.requestCount / limit) * 100;
        state.isNearLimit = state.percentage >= 80;
        state.isOverLimit = state.percentage >= 100;
      }
    },
    // Update configuration locally
    updateLocalConfig: (state, action) => {
      state.config = { ...state.config, ...action.payload };
      // Recalculate derived values using actual limit
      const limit = state.currentStats.limit || state.config.visitorRequestLimit;
      if (limit) {
        state.remaining = limit - state.currentStats.requestCount;
        state.percentage = (state.currentStats.requestCount / limit) * 100;
        state.isNearLimit = state.percentage >= 80;
        state.isOverLimit = state.percentage >= 100;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Get Visitor Stats
      .addCase(getVisitorStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVisitorStats.fulfilled, (state, action) => {
        state.loading = false;
        state.currentStats = {
          requestCount: action.payload.requestCount || 0,
          limit: action.payload.limit, // Use actual limit from API
          rateLimit: action.payload.rateLimit, // Use actual rate limit from API
          lastReset: action.payload.lastReset,
          nextReset: action.payload.nextReset,
          lastActivity: action.payload.lastActivity,
          recentRequests: action.payload.recentRequests || []
        };
        
        // Calculate derived values using actual limit
        const limit = state.currentStats.limit || state.config.visitorRequestLimit;
        if (limit) {
          state.remaining = limit - state.currentStats.requestCount;
          state.percentage = (state.currentStats.requestCount / limit) * 100;
          state.isNearLimit = state.percentage >= 80;
          state.isOverLimit = state.percentage >= 100;
        }
      })
      .addCase(getVisitorStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to load visitor stats';
      })

      // Get Visitor Config
      .addCase(getVisitorConfig.pending, (state) => {
        state.configLoading = true;
        state.error = null;
      })
      .addCase(getVisitorConfig.fulfilled, (state, action) => {
        state.configLoading = false;
        state.config = {
          visitorRequestLimit: action.payload.visitorRequestLimit, // Use actual value from API
          visitorRateLimit: action.payload.visitorRateLimit, // Use actual value from API
          trackVisitorRequests: action.payload.trackVisitorRequests
        };
        
        // Recalculate derived values using actual limit
        const limit = state.currentStats.limit || state.config.visitorRequestLimit;
        if (limit) {
          state.remaining = limit - state.currentStats.requestCount;
          state.percentage = (state.currentStats.requestCount / limit) * 100;
          state.isNearLimit = state.percentage >= 80;
          state.isOverLimit = state.percentage >= 100;
        }
      })
      .addCase(getVisitorConfig.rejected, (state, action) => {
        state.configLoading = false;
        state.error = action.payload?.msg || 'Failed to load visitor configuration';
      })

      // Update Visitor Config
      .addCase(updateVisitorConfig.pending, (state) => {
        state.configLoading = true;
        state.error = null;
      })
      .addCase(updateVisitorConfig.fulfilled, (state, action) => {
        state.configLoading = false;
        state.success = action.payload.msg || 'Configuration updated successfully';
        if (action.payload.config) {
          state.config = {
            visitorRequestLimit: action.payload.config.visitorRequestLimit, // Use actual value
            visitorRateLimit: action.payload.config.visitorRateLimit, // Use actual value
            trackVisitorRequests: action.payload.config.trackVisitorRequests
          };
        }
        
        // Recalculate derived values using actual limit
        const limit = state.currentStats.limit || state.config.visitorRequestLimit;
        if (limit) {
          state.remaining = limit - state.currentStats.requestCount;
          state.percentage = (state.currentStats.requestCount / limit) * 100;
          state.isNearLimit = state.percentage >= 80;
          state.isOverLimit = state.percentage >= 100;
        }
      })
      .addCase(updateVisitorConfig.rejected, (state, action) => {
        state.configLoading = false;
        state.error = action.payload?.msg || 'Failed to update visitor configuration';
      })

      // Get Visitors List
      .addCase(getVisitorsList.pending, (state) => {
        state.visitorsLoading = true;
        state.error = null;
      })
      .addCase(getVisitorsList.fulfilled, (state, action) => {
        state.visitorsLoading = false;
        state.visitors = action.payload.visitors || [];
        state.visitorsTotal = action.payload.total || 0;
      })
      .addCase(getVisitorsList.rejected, (state, action) => {
        state.visitorsLoading = false;
        state.error = action.payload?.msg || 'Failed to load visitors list';
      })

      // Reset Visitor Limits
      .addCase(resetVisitorLimits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetVisitorLimits.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.msg || 'Limits reset successfully';
        
        // Reset stats
        state.currentStats.requestCount = 0;
        state.currentStats.lastReset = new Date().toISOString();
        state.currentStats.nextReset = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        state.currentStats.recentRequests = [];
        
        // Recalculate derived values using actual limit
        const limit = state.currentStats.limit || state.config.visitorRequestLimit;
        if (limit) {
          state.remaining = limit;
          state.percentage = 0;
          state.isNearLimit = false;
          state.isOverLimit = false;
        }
      })
      .addCase(resetVisitorLimits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to reset limits';
      });
  }
});

export const { 
  clearMessages, 
  incrementLocalCount, 
  updateLocalConfig 
} = visitorLimitsSlice.actions;

export default visitorLimitsSlice.reducer;